// Release history, shown in the build modal behind the version badge.
//
// `model` records which Claude model implemented the release. This project is
// built almost entirely by AI pair-programming, and knowing which model wrote
// which feature turns out to matter when tracing a regression — different
// models make different classes of mistake. Blank means human-authored.
//
// Keep newest first. Update this in the same change that bumps package.json.

export type ChangeKind = 'feature' | 'fix' | 'docs' | 'chore';

export interface Change {
  kind: ChangeKind;
  text: string;
}

export interface Release {
  version: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  title: string;
  /** Which Claude model built it, or undefined for human-authored work. */
  model?: string;
  changes: Change[];
}

export const CHANGE_LABELS: Record<ChangeKind, string> = {
  feature: 'New',
  fix: 'Fixed',
  docs: 'Docs',
  chore: 'Chore',
};

export const CHANGELOG: Release[] = [
  {
    version: '0.8.0',
    date: '2026-08-06',
    title: 'Pocket edition — safe for anyone to have the link',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: 'A second build of the same app whose defining property is negative: the file-ingest path does not exist in the shipped JavaScript. Not hidden behind a flag — never compiled in. Sample genomes and the full educational layer only, so the link is safe in anyone\'s hands and its explainer says why.',
      },
      {
        kind: 'feature',
        text: 'The full edition\'s loader is now a lazily-loaded chunk, so the main page gets smaller and the ingest code loads only when someone actually goes to load a file.',
      },
      {
        kind: 'fix',
        text: 'The streaming parser built in 0.6.0 was never actually wired to the UI — the loader still read whole files into memory with FileReader. Found because the pocket tree-shaking audit greps the bundles: DecompressionStream was missing from the FULL build. Now wired, with progress, and verified end-to-end by dropping a synthesized .vcf.gz gVCF through the real dropzone.',
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2026-08-06',
    title: 'Nucleus — every variant in 3D',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: 'A 3D tab rendering every position in your file as a point in space. Each chromosome is a coil radiating from the centre, because interphase chromosomes really do occupy separate territories. Drag to orbit, scroll to fly in until individual variants separate.',
      },
      {
        kind: 'feature',
        text: 'Brightness is density — additive blending means overlapping points accumulate, so the glow shows where your test sampled most heavily and the dark gaps are the centromeres probes cannot bind.',
      },
      {
        kind: 'feature',
        text: '"Condition markers only" collapses the view to the ~178 catalog markers. It goes nearly black, which is the honest picture of how little of a genome any interpretation tool reads.',
      },
      {
        kind: 'chore',
        text: 'Raw WebGL2, no three.js — that would have added ~600 kB gzipped for one view. The whole renderer is two draw calls and a 60-line matrix helper.',
      },
    ],
  },
  {
    version: '0.6.1',
    date: '2026-08-06',
    title: 'Genome map, ancestry samples, and streaming ingest',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: 'Map tab — a to-scale ideogram of all 24 chromosomes with real UCSC cytogenetic bands, your variant density, and the catalog markers your file actually contains. Both GRCh37 and GRCh38 bands are bundled, because array exports and VCFs disagree on coordinates.',
      },
      {
        kind: 'feature',
        text: 'Sample genomes for all five 1000 Genomes superpopulations. Genotypes are drawn under Hardy-Weinberg from real per-population allele frequencies, so the demo now teaches something true: ALDH2 flush appears only in the East Asian sample, PTPN22 only in the European, sickle trait only in the African.',
      },
      {
        kind: 'feature',
        text: 'Streaming, gzip-aware file ingest for sequencing-provider data. Multi-gigabyte .vcf.gz loads without ever holding the file in memory, with progress; gVCF reference blocks are recognised and skipped rather than counted as millions of variants.',
      },
      {
        kind: 'feature',
        text: '"What is this?" panels on the individual Overview metrics — variants, call rate, heterozygosity, Ti/Tv.',
      },
      {
        kind: 'fix',
        text: 'The sample generator drew positions from GRCh38 lengths while declaring itself a GRCh37 array export, putting markers past the ends of several chromosomes on the ideogram.',
      },
      {
        kind: 'fix',
        text: 'An unbounded position-dedupe loop hung the tab whenever a chromosome length was missing — which MT now is, since the cytoband table has no mitochondrial entry.',
      },
      {
        kind: 'fix',
        text: 'CSP: the build now throws if the <!--csp--> placeholder ever goes missing instead of silently shipping a page with no policy. Removed two connect-src wildcards that were invalid syntax and being ignored.',
      },
      {
        kind: 'feature',
        text: 'Reference assembly is now read from the VCF header rather than guessed — ##contig lengths are decisive (GRCh37 chr1 is 249,250,621; GRCh38 is 248,956,422), with ##reference as a fallback. The Map tab says whether the build was read or assumed.',
      },
      {
        kind: 'fix',
        text: 'Light-mode users got a dark flash on every load: the theme was applied from a deferred module script, after first paint. A parser-blocking classic script in <head> now sets it first — external rather than inline, so script-src stays \'self\'.',
      },
    ],
  },
  {
    version: '0.5.1',
    date: '2026-08-05',
    title: 'Adversarial review of the expansion — 36 corrections',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'fix',
        text: 'CRITICAL: rs6841581 (intracranial aneurysm) named the protective allele as the risk allele. G/G — the highest-risk genotype and ~73% of Europeans — was being told it carried zero risk copies.',
      },
      {
        kind: 'fix',
        text: 'The asthma summary had the onset direction backwards: childhood-onset asthma carries the allergy/Th2 architecture and roughly 3× the SNP heritability, not adult-onset.',
      },
      {
        kind: 'fix',
        text: 'Psoriasis and eczema do not have "non-overlapping genetics" — they share loci where the same allele pushes in opposite directions, which is a more interesting fact than the one originally claimed.',
      },
      {
        kind: 'fix',
        text: 'Three markers were scored as risk or protective when they are neither (MTHFR A1298C, a TSH variant, CYP3A5 under codeine) — they now use the neutral trait direction and no longer count as hits.',
      },
      {
        kind: 'fix',
        text: 'Corrected allele frequencies, ancestry caveats, star-allele names and effect sizes across 30 further entries — including a vitamin-D claim retracted by the literature and an African-ancestry frequency reported as 5% when it is 95%.',
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-08-05',
    title: 'Catalog expansion — 29 conditions to 103',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: '74 new conditions, 178 markers total. Dermatology and atopic disease led the expansion: psoriasis, atopic dermatitis, psoriatic arthritis, asthma, vitiligo, alopecia areata, acne, rosacea, allergic rhinitis.',
      },
      {
        kind: 'feature',
        text: 'Also autoimmune (lupus, ulcerative colitis, Graves\', Hashimoto\'s, ankylosing spondylitis), cardiometabolic (NAFLD, gallstones, Lp(a), CKD), pharmacogenomics (abacavir, allopurinol, thiopurines, DPYD), cancer, neuro/psych and traits.',
      },
      {
        kind: 'feature',
        text: 'Two new categories: Skin and Respiratory.',
      },
      {
        kind: 'chore',
        text: 'Catalog split out of conditions.ts into catalog.ts (data) and conditions-types.ts (types), so the matching logic stays readable. Callers still import from ./lib/conditions.',
      },
      {
        kind: 'chore',
        text: 'Every entry machine-validated before integration for the invariant that broke rs334 — effectAllele must be one of alleles — plus allele validity, rsID format and id uniqueness.',
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-08-05',
    title: 'Vault tab — note queue and ideas ledger',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: 'A note queue. The ＋ note buttons now stage into the vault as well as copying, so several notes can be exported together instead of one clipboard at a time.',
      },
      {
        kind: 'feature',
        text: 'An ideas ledger for questions and hunches, exportable as one dated markdown note.',
      },
      {
        kind: 'feature',
        text: 'Local downloads — notes save as .md via an in-memory Blob. No network involved, so connect-src \'none\' does not block it.',
      },
      {
        kind: 'chore',
        text: 'Deliberate persistence split: queued notes are memory-only because they embed genotype calls; the ideas ledger persists because it is your own prose. Verified across a reload.',
      },
      {
        kind: 'fix',
        text: 'Two dev-CSP tokens were invalid syntax — a CSP host-source only allows * as the leftmost label, so ws://192.168.*.*:* was silently ignored.',
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-08-05',
    title: 'Explainers, re-identification, and a build modal',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: '"What is this?" panels on every card — a plain-English hook, the real concept with real terminology, how to read the numbers on screen, and an honest statement of limits.',
      },
      {
        kind: 'feature',
        text: 'A re-identification explainer: why ~75 common SNPs uniquely identify a person, why relative-matching is the channel you cannot close, and why de-identifying a genome does not work.',
      },
      {
        kind: 'feature',
        text: 'This modal. The version badge now opens the changelog plus the exact commit the running build came from.',
      },
      {
        kind: 'fix',
        text: 'The explainer dialog rendered entirely in capitals — it sits inside a card <h2>, and text-transform inherits.',
      },
    ],
  },
  {
    version: '0.2.1',
    date: '2026-08-05',
    title: 'Adversarial review of the condition catalog',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'fix',
        text: 'CRITICAL: rs334 (sickle cell) had the effect allele inverted. On the forward strand T is the normal reference and A is HbS, so an unaffected person read as "2 copies of T (risk)" while a true HbS homozygote scored zero.',
      },
      {
        kind: 'fix',
        text: 'CRITICAL: homozygous calls on palindromic (A/T, C/G) SNPs were given a confident copy count, which is a coin flip between strands. They now report both possibilities. Heterozygous palindromic calls turn out to be orientation-invariant, so those resolve cleanly.',
      },
      {
        kind: 'fix',
        text: 'A leading no-call could mask a real genotype for duplicate rsIDs, silently dropping a heterozygous C282Y.',
      },
      {
        kind: 'fix',
        text: 'rs2981582 alleles were reverse-complemented; CYP1A2*1F was labelled on the wrong allele; the Parkinson\'s and APOE entries each contained a false claim.',
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-08-05',
    title: 'Conditions, light mode, and a dev/prod CSP split',
    model: 'Claude Opus 5 (1M context)',
    changes: [
      {
        kind: 'feature',
        text: 'Conditions tab — a bundled catalog of replicated SNP-condition associations with genotype matching and real strand-orientation detection. No aggregate risk score, deliberately.',
      },
      { kind: 'feature', text: 'Light mode, with every colour moved to a token and no flash on load.' },
      {
        kind: 'fix',
        text: 'The strict CSP killed hot-reload on every edit. The build still ships connect-src \'none\'; dev gets a loopback-only exception.',
      },
      {
        kind: 'feature',
        text: 'ISENG_HOST / npm run dev:phone serves over Tailscale so the app opens on a phone.',
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-08-05',
    title: 'Privacy-first genome browser scaffold',
    changes: [
      { kind: 'feature', text: 'Drag-and-drop loader for 23andMe / AncestryDNA raw text and VCF.' },
      { kind: 'feature', text: 'Overview, Browse and Analysis views with per-chromosome and QC metrics.' },
      { kind: 'feature', text: 'Obsidian export to a local-only vault.' },
    ],
  },
];
